import { CastorService } from '@aipb/gulliver/castor/data-access';
import { CastorConfigRepository } from '@aipb/gulliver/catalog/data-access';
import { Topic, TopicRegisterItem } from '@aipb/gulliver/catalog/model';
import { uniqueId } from '@aipb/shared/util/other';
import { selectLoading, updateLoading, withLoading } from '@aipb/shared/util/state';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { finalize, map, tap } from 'rxjs/operators';

interface TopicItem {
  label: string;
  count: number;
}

interface TopicProps {
  id: string;
  items: TopicRegisterItem[];
  coreId: string;
}

@Injectable()
export class TopicRepository {
  castorConfigRepository = inject(CastorConfigRepository);
  store = createStore(
    { name: 'topic-' + uniqueId() },
    withLoading(),
    withProps<TopicProps>({ id: '', items: [], coreId: this.castorConfigRepository.getActiveCoreId() })
  );
  loading$ = this.store.pipe(selectLoading());
  items$ = this.store.pipe(select((state) => state.items));
  coreId$ = this.store.pipe(select((state) => state.coreId));

  get coreId() {
    return this.store.getValue().coreId;
  }

  setId(id: string) {
    this.store.update((state) => ({
      ...state,
      id
    }));
  }

  setItems(items: TopicItem[]) {
    this.store.update((state) => ({
      ...state,
      items
    }));
  }

  setCoreId(coreId: string) {
    this.store.update((state) => ({
      ...state,
      coreId
    }));
  }

  setLoading(loading: boolean) {
    this.store.update(updateLoading(loading));
  }

  reset() {
    this.store.reset();
  }
}

@Injectable()
export class TopicService {
  constructor(private repository: TopicRepository, private castor: CastorService) {}

  public fetch(topic: Topic, coreId: string) {
    this.repository.setLoading(true);
    return this.castor
      .register({
        coreId: coreId,
        facet: {
          field: topic.fieldName,
          limit: topic.limit || 100,
          sort: topic.sort || 'index'
        },
        query: topic.solrQuery || '*:*'
      })
      .pipe(
        map((data) => {
          return data.map((ar) => {
            const key = Object.keys(ar)[0];
            return <TopicItem>{
              label: key,
              count: +ar[key]
            };
          });
        }),
        tap((data) => {
          this.repository.setId(topic.id);
          this.repository.setItems(data);
        }),
        finalize(() => {
          this.repository.setLoading(false);
        })
      );
  }
}
